import React, { useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
import { Autoplay, Pagination } from 'swiper/modules';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import { FullyCentered } from 'styled/alignment/Center';
import Text from 'components/_UI/Text';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';

const EstablishmentCarousel = ({ data, onSlideClick }) => {
    const swiperRef = useRef(null);

    const handleNext = () => swiperRef.current?.slideNext();
    const handlePrev = () => swiperRef.current?.slidePrev();

    const handleSlideClick = (item, index) => {
        if (onSlideClick) {
            onSlideClick(item);
        }
    };

    if (!data?.length) return null;

    return (
        <CarouselWrapper>
            <ArrowLeft onClick={handlePrev}>
                <ChevronLeft size={20} color={colors.night} />
            </ArrowLeft>
            <ArrowRight onClick={handleNext}>
                <ChevronRight size={20} color={colors.night} />
            </ArrowRight>

            <CarouselContainer>
                <StyledSwiper
                    loop={data.length >= 6}
                    breakpoints={{
                        0: { slidesPerView: 1, spaceBetween: 0, centeredSlides: false },
                        1200: { slidesPerView: 3, spaceBetween: 12, centeredSlides: true },
                    }}
                    autoplay={{
                        delay: 4000,
                        disableOnInteraction: false,
                    }}
                    pagination={{ clickable: true }}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    modules={[Autoplay, Pagination]}
                >
                    {data.map((item, index) => (
                        <SwiperSlide key={item.slug} onClick={() => handleSlideClick(item, index)}>
                            <Content>
                                <ImageWrapper>
                                    <BackgroundImage
                                        src={item?.backgroundImage?.path || '/assets/images/placeholder-events.png'}
                                        alt={item?.name}
                                        onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                                    />

                                    <TopBadgesContainer>
                                        <Badge isOpen={item?.open || item?.dates?.open}>
                                            <Text name='mini' color={colors.neutralLight} paddingBottom={0}>
                                                {(item?.open || item?.dates?.open) ? 'Aberto' : 'Fechado'}
                                            </Text>
                                        </Badge>
                                    </TopBadgesContainer>
                                </ImageWrapper>

                                <ProfileSection>
                                    <ProfileImageBorder>
                                        <DynamicProfileImage
                                            img={item?.profileImage?.path}
                                            word={item?.name}
                                            size='42px'
                                            textSize='small'
                                        />
                                    </ProfileImageBorder>
                                </ProfileSection>

                                <CardContent>
                                    <Text
                                        type='bold'
                                        color={colors.nightDark}
                                        ellipsisAt='95%'
                                        paddingBottom={0}
                                        name='small'
                                    >
                                        {item?.name}
                                    </Text>

                                    {(item?.address?.name || item?.address?.street) && (
                                        <Row verticalCenter>
                                            <MapPin size={12} color={colors.nightLight} />
                                            <Text name='mini' ellipsisAt='95%' marginLeft='3px' paddingBottom={0}>
                                                {item?.address?.name
                                                    ? `${item.address.name}`
                                                    : item?.address?.street
                                                    ? `${item.address.street}`
                                                    : ''}
                                            </Text>
                                        </Row>
                                    )}

                                    {item?.tagsEstablishments?.length > 0 && (
                                        <Row>
                                            <Text name='mini' ellipsisAt='95%' paddingBottom='0px' marginLeft='3px'>
                                                {item.tagsEstablishments.map(tag => `#${tag.name} `)}
                                            </Text>
                                        </Row>
                                    )}
                                </CardContent>
                            </Content>
                        </SwiperSlide>
                    ))}
                </StyledSwiper>
            </CarouselContainer>

        </CarouselWrapper>
    );
};

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const CarouselWrapper = styled.div`
    position: relative;
    margin-top: -14px;
    animation: ${fadeInUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const CarouselContainer = styled.div`
    width: 100%;
    overflow: hidden;
    position: relative;
`;

const StyledSwiper = styled(Swiper)`
    width: 100%;
    height: auto;
    padding-top: 8px;
    padding-bottom: 4px;

    .swiper-slide {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        transition: transform 0.35s ease, opacity 0.35s ease, filter 0.35s ease;
        cursor: grab;
        transform: scale(0.84);
        opacity: 0.55;
        filter: brightness(0.88);
    }

    .swiper-slide-active {
        transform: scale(1.06);
        opacity: 1;
        filter: brightness(1);
        z-index: 3;
        cursor: pointer;
    }

    .swiper-slide-next,
    .swiper-slide-prev {
        transform: scale(0.93);
        opacity: 0.82;
        filter: brightness(0.94);
        z-index: 2;
    }

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        .swiper-slide {
            transform: none;
            opacity: 1;
            filter: none;
        }
        .swiper-slide-active,
        .swiper-slide-next,
        .swiper-slide-prev {
            transform: none;
            opacity: 1;
            filter: none;
        }
    }

    .swiper-pagination {
        position: relative;
        margin-top: 20px;
        text-align: center;
    }

    .swiper-pagination-bullet {
        background: ${colors.nightLight};
        opacity: 0.6;
        width: 8px;
        height: 8px;
        margin: 0 5px;
        border-radius: 50%;
        transition: opacity 0.3s;
        cursor: pointer;
    }

    .swiper-pagination-bullet-active {
        background: ${colors.occasionalPurple};
        opacity: 1;
    }
`;

const ArrowLeft = styled(FullyCentered)`
    position: absolute;
    left: 18px;
    top: 40%;
    transform: translateY(-50%);
    transition: all ease-in-out .2s;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}22;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    cursor: pointer;
    z-index: 4;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:hover {
        border-color: ${colors.occasionalPurple};
        svg { stroke: ${colors.occasionalPurple}; }
    }

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) { display: none; }
`;

const ArrowRight = styled(FullyCentered)`
    position: absolute;
    right: 22px;
    top: 40%;
    transform: translateY(-50%);
    transition: all ease-in-out .2s;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}22;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    cursor: pointer;
    z-index: 4;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:hover {
        border-color: ${colors.occasionalPurple};
        svg { stroke: ${colors.occasionalPurple}; }
    }

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) { display: none; }
`;

const Content = styled(Column)`
    width: 100%;
    cursor: pointer;
    position: relative;
    background: transparent;

    &:hover img:first-child {
        transform: translateY(-2px);
        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.12);
    }
`;

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 2;
    border-radius: 12px;
    overflow: hidden;
`;

const BackgroundImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    border-radius: 12px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
`;

const TopBadgesContainer = styled(Row)`
    position: absolute;
    top: 10px;
    left: 8px;
    width: auto;
    z-index: 2;
`;

const Badge = styled(FullyCentered)`
    padding: 4px 8px;
    border-radius: 6px;
    margin-right: 4px;
    width: auto;
    background: ${({ isOpen }) => isOpen ? 'rgba(43, 200, 102, 0.85)' : 'rgba(231, 80, 90, 0.85)'};
`;

const ProfileSection = styled(FullyCentered)`
    margin-top: -22px;
    z-index: 2;
    position: relative;
`;

const ProfileImageBorder = styled(FullyCentered)`
    width: 46px;
    height: 46px;
    border: 3px solid ${colors.neutralLight};
    border-radius: 50%;
    background: ${colors.neutralLight};
    overflow: hidden;
`;

const CardContent = styled(Column)`
    padding: 6px 4px 4px;
    gap: 3px;
    align-items: center;
    text-align: center;
`;

export default EstablishmentCarousel;
